import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MatrixGroup,
  MatrixGroupRequest,
  MatrixNode,
  matrixConfigurationApi,
} from '@texas/api/endpoints/matrixConfigurationApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { useActiveSession } from '@texas/hooks/useSession';

export function MatrixGroupModal({
  canEdit,
  group,
  isOpen,
  onClose,
  onSaved,
}: {
  canEdit: boolean;
  group: MatrixGroup | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const toast = useToast();
  const [form, setForm] = useState<MatrixGroupRequest>({
    name: '',
    displayOrder: 0,
    matrixNodeId: 0,
    matrixNodeValues: [],
  });
  const {
    data: nodes,
    refetch: loadNodes,
    error: nodesError,
  } = useApiResource(matrixConfigurationApi.getNodes);
  const {
    data: loadedGroup,
    refetch: loadGroup,
    loading: loadingGroup,
    error: groupError,
  } = useApiResource(matrixConfigurationApi.getGroup);
  const {
    request: create,
    loading: creating,
    error: createError,
  } = useApiRequest(matrixConfigurationApi.createGroup);
  const {
    request: update,
    loading: updating,
    error: updateError,
  } = useApiRequest(matrixConfigurationApi.updateGroup);
  const {
    request: remove,
    loading: deleting,
    error: deleteError,
  } = useApiRequest(matrixConfigurationApi.deleteGroup);

  useEffect(() => {
    if (!isOpen) return;
    loadNodes(currentUser.organizationId);
    if (group) loadGroup(group.id);
  }, [currentUser.organizationId, group, isOpen, loadGroup, loadNodes]);

  useEffect(() => {
    if (!isOpen) return;
    if (group && loadedGroup?.id !== group.id) return;
    const source = group ? loadedGroup : null;
    setForm({
      name: source?.name ?? '',
      displayOrder: source?.displayOrder ?? 0,
      matrixNodeId: source?.matrixNodeId ?? nodes?.[0]?.id ?? 0,
      matrixNodeValues:
        source?.matrixNodeValues.map((value) => ({ ...value })) ?? [],
    });
  }, [group, isOpen, loadedGroup, nodes]);

  const selectedNode = (nodes ?? []).find(
    (node) => node.id === form.matrixNodeId,
  );
  const isGroupReady = !group || loadedGroup?.id === group.id;

  const save = async () => {
    if (!isGroupReady) return;
    const response = group
      ? await update(group.id, form)
      : await create(currentUser.organizationId, form);
    if (response.hasError) return;
    toast({
      title: group ? t('general.updated') : t('general.created'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  const deleteGroup = async () => {
    if (!group || !window.confirm(t('configuration.deleteMatrixGroup'))) return;
    const response = await remove(group.id);
    if (response.hasError) return;
    toast({
      title: t('general.successfullyRemoved'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  const toggleValue = (id: number, checked: boolean) => {
    setForm((current) => ({
      ...current,
      matrixNodeValues: checked
        ? [...current.matrixNodeValues, { id }]
        : current.matrixNodeValues.filter((value) => value.id !== id),
    }));
  };

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {group ? group.name : t('configuration.newMatrixGroup')}
        </ModalHeader>
        <ModalBody>
          <ErrorDetails
            error={
              nodesError ??
              groupError ??
              createError ??
              updateError ??
              deleteError
            }
          />
          <Flex direction="column" gap="4">
            <FormControl isRequired={true}>
              <TexasFormLabel>{t('general.name')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                value={form.name}
                isDisabled={!canEdit}
                maxLength={256}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />
              <FormCounter length={form.name.length} maxLength={256} />
            </FormControl>
            <FormControl>
              <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
              <Input
                type="number"
                variant="entityDirectory"
                value={form.displayOrder}
                isDisabled={!canEdit}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    displayOrder: Number(event.target.value),
                  }))
                }
              />
            </FormControl>
            <FormControl isRequired={true}>
              <TexasFormLabel>{t('configuration.matrixNode')}</TexasFormLabel>
              <Select
                variant="entityDirectory"
                value={form.matrixNodeId}
                isDisabled={!!group || !canEdit}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    matrixNodeId: Number(event.target.value),
                    matrixNodeValues: [],
                  }))
                }
              >
                {(nodes ?? []).sort(sortNodes).map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <Table variant="entityDirectoryCompact" size="sm">
              <Thead>
                <Tr>
                  <Th>{t('general.value')}</Th>
                  <Th>{t('configuration.groupValueName')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(selectedNode?.matrixNodeValues ?? [])
                  .filter((value) => !value.archived)
                  .sort((left, right) => left.displayOrder - right.displayOrder)
                  .map((value) => {
                    const selectedValue = form.matrixNodeValues.find(
                      (item) => item.id === value.id,
                    );
                    return (
                      <Tr key={value.id}>
                        <Td>
                          <Checkbox
                            isChecked={!!selectedValue}
                            isDisabled={!canEdit}
                            onChange={(event) =>
                              toggleValue(value.id, event.target.checked)
                            }
                          >
                            {value.identifier}: {value.name}
                          </Checkbox>
                        </Td>
                        <Td>
                          {selectedValue && (
                            <>
                              <Input
                                size="sm"
                                variant="entityDirectory"
                                maxLength={20}
                                value={selectedValue.name ?? value.name}
                                isDisabled={!canEdit}
                                onChange={(event) =>
                                  setForm((current) => ({
                                    ...current,
                                    matrixNodeValues:
                                      current.matrixNodeValues.map((item) =>
                                        item.id === value.id
                                          ? {
                                              ...item,
                                              name: event.target.value,
                                            }
                                          : item,
                                      ),
                                  }))
                                }
                              />
                              <FormCounter
                                length={
                                  (selectedValue.name ?? value.name).length
                                }
                                maxLength={20}
                              />
                            </>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
              </Tbody>
            </Table>
          </Flex>
        </ModalBody>
        <ModalFooter gap="2">
          {group && canEdit && (
            <Button
              colorScheme="red"
              variant="ghost"
              isLoading={deleting}
              onClick={deleteGroup}
            >
              {t('general.remove')}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="entityDirectoryPrimary"
              isLoading={creating || updating}
              isDisabled={!isGroupReady || loadingGroup}
              onClick={save}
            >
              {t('general.save')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function sortNodes(left: MatrixNode, right: MatrixNode) {
  return (
    left.displayOrder - right.displayOrder ||
    left.name.localeCompare(right.name)
  );
}
